import { useState } from "react";
import { useNavigate } from "react-router";

import { Button } from "../../../common/Atoms/Button";
import { ContactTableFields, useContactTableModels } from "../../hooks/useContacts";
import { useUser } from "../../hooks/useUser";
import { useMobileNumberCheck } from "../../hooks/useMobileNumberCheck";
import { useModal } from "../../hooks/useModals";
import { useGetSelectedOrganisation } from "../../hooks/useGetSelectedOrganisation";
import { useStartMobileNumberCheckMutation } from "../../services/api/mobileNumberCheckApi/mobileNumberCheck";
import { Modal } from "../../../common/Atoms/Modal";
import { Paragraph } from "../../../common/Atoms/Typography/Paragraph";

type ContactQuickFilterType = `has_balance` | `assigned_to_me` | `invalid_mobile`;

// Contact quick filters that aren't age ranges. Used by the desktop nested
// sidebar and the mobile sidebar.
export function ContactQuickFiltersNav({ buttonClassName, onNavigate }: { buttonClassName: string; onNavigate?: () => void }) {
  const { setFilterModel, setSortModel } = useContactTableModels();
  const { data: userData } = useUser();
  const navigate = useNavigate();

  const currentUserId = userData?.id;

  const currentOrg = useGetSelectedOrganisation();
  const { runningCheck, lastRun } = useMobileNumberCheck();
  const { show: showProgressModal } = useModal(`mobileNumberCheck`);
  const [startCheck, { isLoading: startingCheck }] = useStartMobileNumberCheckMutation();
  const [invalidMobileModalOpen, setInvalidMobileModalOpen] = useState(false);

  // The invalid flags are only as fresh as the last check, so suggest one first unless it ran in the last hour
  function onInvalidMobile() {
    if (runningCheck || !lastRun) {
      setInvalidMobileModalOpen(true);

      return;
    }

    onQuickFilter(`invalid_mobile`);
  }

  async function onRunCheck() {
    const result = await startCheck(currentOrg.id);

    setInvalidMobileModalOpen(false);

    if (!(`error` in result)) {
      showProgressModal();
    }
  }

  function onFilterAnyway() {
    setInvalidMobileModalOpen(false);
    onQuickFilter(`invalid_mobile`);
  }

  function onQuickFilter(type: ContactQuickFilterType) {
    if (type === `has_balance`) {
      setFilterModel({
        items: [
          {
            field: ContactTableFields.outstandingBalance,
            operator: `>=`,
            value: 0.01,
            id: Math.round(Math.random() * 1e5),
          },
        ],
      });

      setSortModel([
        {
          field: ContactTableFields.outstandingBalance,
          sort: `desc`,
        },
      ]);
    }

    if (type === `assigned_to_me` && currentUserId) {
      setFilterModel({
        items: [
          {
            field: ContactTableFields.assignee,
            operator: `is`,
            value: currentUserId,
            id: Math.round(Math.random() * 1e5),
          },
        ],
      });
    }

    if (type === `invalid_mobile`) {
      setFilterModel({
        items: [
          {
            field: ContactTableFields.mobileNumber,
            operator: `isInvalid`,
            id: Math.round(Math.random() * 1e5),
          },
        ],
      });
    }

    // Quick filters replace any active saved filter, so land on the page
    // without its savedFilterId (this also puts the columns back to the
    // default spec)
    navigate(`/${currentOrg?.id}/contacts/manage`);
    onNavigate?.();
  }

  return (
    <>
      <button
        type={ `button` }
        onClick={ () => onQuickFilter(`has_balance`) }
        className={ buttonClassName }
      >
        { `Has balance` }
      </button>
      { currentUserId && (
        <button
          type={ `button` }
          onClick={ () => onQuickFilter(`assigned_to_me`) }
          className={ buttonClassName }
        >
          { `Assigned to me` }
        </button>
      ) }
      <button
        type={ `button` }
        onClick={ onInvalidMobile }
        className={ buttonClassName }
      >
        { `Invalid mobile` }
      </button>

      <Modal
        open={ invalidMobileModalOpen }
        setOpen={ setInvalidMobileModalOpen }
        title={ runningCheck ? `Mobile number check in progress` : `Check mobile numbers first?` }
      >
        <Paragraph>
          {
            runningCheck ?
              `A mobile number check is running right now, so the results of this filter may change until it finishes.` :
              `Mobile numbers haven't been checked in the last hour. We recommend running the check first so this filter shows up to date results.`
          }
        </Paragraph>

        <div className={ `flex justify-end space-x-2 mt-4` }>
          <Button
            onClick={ onFilterAnyway }
            color={ runningCheck ? `paidniceGreen` : `white` }
          >
            { `Filter anyway` }
          </Button>
          { !runningCheck && (
            <Button
              onClick={ onRunCheck }
              loading={ startingCheck }
              disabled={ startingCheck }
            >
              { `Run check` }
            </Button>
          ) }
        </div>
      </Modal>
    </>
  );
}
